<template>
  <DashboardContentLayout>
    <template
      v-if="!isLoading"
      #title>
      <span class="text-24">
        {{ fullName || $t('pages.onboarding.admin_page.not_specified') }}
      </span>
      <AppBadge
        class="ml-12"
        :status="applicationStatus?.toLowerCase()"/>
    </template>
    <template v-if="!isLoading">
      <TabLinks
        :tabs="tabs"
        secondary/>
      <VerifyMessage
        v-if="isNotVerified && isUserProfile"
        :sections="[]"
        customer-type="customer"
        @submit="submitApplication"/>
    </template>
    <router-view v-slot="{ Component }">
      <transition name="page">
        <template v-if="!isLoading">
          <component :is="Component"/>
        </template>
        <template v-else>
          <LoaderStub class="items-center"/>
        </template>
      </transition>
    </router-view>
  </DashboardContentLayout>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue';
import { useRoute } from 'vue-router';

import LoaderStub from '@/components/loader-stub.vue';
import AppBadge from '@/components/ui-kit/app-badge.vue';
import TabLinks from '@/components/ui-kit/app-tab-links.vue';
import { translateKey } from '@/i18n/translate';
import DashboardContentLayout from '@/layouts/dashboard/dashboard-content-layout.vue';
import { ONBOARDING_PERSONAL_PROFILE_CHILDREN as tabs } from '@/modules/onboarding-personal-profile/routes';
import VerifyMessage
  from '@/modules/onboarding-user-profile/pages/user-profile-and-business/components/verify-message.vue';
import { OnboardingAdminRequests } from '@/services/requests';
import { usePersonalOnboardingStore } from '@/stores';
import { fetchApplication } from '@/stores/OnboardingPersonal';
import { errorNotification, successNotification } from '@/utils';

const { applicationStatus, personalDetails } = usePersonalOnboardingStore();

const route = useRoute();

const isLoading = ref(true);

const isNotVerified = computed(() => applicationStatus?.value === 'NON_VERIFIED');
const isUserProfile = computed(() => route.name === 'onboarding-personal-details');
const { params: { id } } = useRoute();

const fullName = computed(() => {
  if (!personalDetails.value?.firstName && !personalDetails.value?.lastName) {
    return '';
  }

  return [
    personalDetails.value?.firstName,
    personalDetails.value?.lastName,
  ].join(' ');
});

async function readUserApplication() {
  isLoading.value = true;
  await fetchApplication('admin', true, id);
  isLoading.value = false;
}
async function submitApplication() {
  isLoading.value = true;
  const { error } = await OnboardingAdminRequests.Individual.approveApplication(id);
  isLoading.value = false;

  if (error) {
    errorNotification(error);
    return;
  }

  await readUserApplication();

  successNotification(
    translateKey(
      'pages.onboarding.business.company_details.verify.tooltip.success',
      { customer: 'customer' },
    ),
  );
}

onBeforeMount(readUserApplication);
</script>
